import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Shield,
  Building2,
  DollarSign,
  Users,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Heart,
  FileText,
  Clock,
  ClipboardList,
  HelpCircle,
  Moon,
  Sun
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/hooks/useTheme';
import { loadFeatureFlags } from '@/components/utils/featureFlags';

export default function Welcome() {
  const [loading, setLoading] = useState(true);
  const { isDark, toggleTheme } = useTheme();
  const [featureFlags, setFeatureFlags] = useState({
    allow_company_registration: true,
    allow_individual_registration: true
  });

  useEffect(() => {
    checkAuth();
    loadFlags();
  }, []);

  const loadFlags = async () => {
    try {
      const flags = await loadFeatureFlags();
      setFeatureFlags(flags);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  };

  const checkAuth = async () => {
    try {
      const isAuth = await api.auth.isAuthenticated();
      if (isAuth) {
        const user = await api.auth.me();
        if (user) {
          // Redirect based on role and onboarding status
          if (user.user_role === 'admin') {
            window.location.href = createPageUrl('AdminDashboard');
          } else if (user.user_role === 'sub_admin') {
            window.location.href = createPageUrl('EmployerDashboard');
          } else if (user.user_role === 'employer_hr') {
            // Check if employer setup is complete
            if (user.employer_id) {
              window.location.href = createPageUrl('EmployerDashboard');
            } else {
              window.location.href = createPageUrl('CompanyOnboarding');
            }
          } else if (user.user_role === 'employee') {
            // Check if employee is linked
            if (user.employee_id) {
              window.location.href = createPageUrl('EmployeeDashboard');
            } else {
              window.location.href = createPageUrl('InviteCodeEntry');
            }
          } else {
            // No role assigned yet - ask user to select
            window.location.href = createPageUrl('RoleSelection');
          }
        }
      }
    } catch (err) {
      // Not authenticated, stay on welcome page
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </Button>
            <div className="hidden md:flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => (window.location.href = '/me')}
              >
                For Employees
              </Button>
              <Button
                variant="ghost"
                onClick={() => (window.location.href = '/hr')}
              >
                For HR
              </Button>
              <Button
                variant="ghost"
                onClick={() => (window.location.href = '/why')}
              >
                Why BenefitsPro
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => api.auth.redirectToLogin()}
            >
              Sign In
            </Button>
            {featureFlags.allow_company_registration && (
              <Button
                size="sm"
                onClick={() => window.location.href = '/CompanySignup'}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                For Employers
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-4xl mx-auto"
        >
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 text-sm px-4 py-2">
            <Sparkles className="w-4 h-4 mr-2" />
            Section 125 Benefits Management
          </Badge>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
            Maximize Your Benefits, <br />
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              Minimize Your Taxes
            </span>
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            The complete Section 125 benefits platform that helps employees save on taxes 
            while making benefits management effortless for employers.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {featureFlags.allow_company_registration && (
              <Button 
                size="lg"
                onClick={() => window.location.href = '/CompanySignup'}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg px-8 py-6"
              >
                For Companies
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => api.auth.redirectToLogin()}
              className="text-lg px-8 py-6"
            >
              Sign In
            </Button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-3 gap-8 mt-20"
        >
          <div className="text-center">
            <div className="text-4xl font-bold text-emerald-600 mb-2">$2,500+</div>
            <p className="text-muted-foreground">Average Annual Tax Savings</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-emerald-600 mb-2">94%</div>
            <p className="text-muted-foreground">Employee Satisfaction</p>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-emerald-600 mb-2">10 min</div>
            <p className="text-muted-foreground">Average Enrollment Time</p>
          </div>
        </motion.div>
      </section>

      {/* Start Here */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-foreground mb-4">Start Here</h2>
            <p className="text-xl text-muted-foreground">
              Choose the path that matches your role.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Employees',
                description: 'Accept an invite, enter your code, and enroll in minutes.',
                href: '/me',
                cta: 'Employee overview',
                color: 'from-blue-400 to-cyan-500'
              },
              {
                icon: ClipboardList,
                title: 'HR Teams',
                description: 'Set up enrollment, invite employees, and track completion.',
                href: '/hr',
                cta: 'HR overview',
                color: 'from-emerald-400 to-teal-500'
              },
              {
                icon: Building2,
                title: 'Companies',
                description: 'See why companies adopt BenefitsPro for Section 125 benefits.',
                href: '/why',
                cta: 'Why BenefitsPro',
                color: 'from-purple-400 to-pink-500'
              }
            ].map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * idx }}
              >
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardContent className="p-8">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-4`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground mb-6">{item.description}</p>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => (window.location.href = item.href)}>
                        {item.cta}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      {item.title === 'Employees' && featureFlags.allow_individual_registration && (
                        <Button
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                          onClick={() => api.auth.redirectToLogin('/InviteCodeEntry')}
                        >
                          Accept invite
                        </Button>
                      )}
                      {item.title === 'HR Teams' && featureFlags.allow_company_registration && (
                        <Button
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                          onClick={() => (window.location.href = '/CompanySignup')}
                        >
                          Get started
                        </Button>
                      )}
                      {item.title === 'Companies' && featureFlags.allow_company_registration && (
                        <Button
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                          onClick={() => (window.location.href = '/CompanySignup')}
                        >
                          Company setup
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-muted/30 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-foreground mb-4">Everything You Need</h2>
            <p className="text-xl text-muted-foreground">Comprehensive benefits management in one platform</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: DollarSign,
                title: 'Tax Savings Calculator',
                description: 'Instantly calculate how much employees can save with Section 125 pre-tax benefits',
                color: 'from-emerald-400 to-teal-500'
              },
              {
                icon: Users,
                title: 'Employee Portal',
                description: 'Beautiful, intuitive interface for employees to browse and enroll in benefits',
                color: 'from-blue-400 to-cyan-500'
              },
              {
                icon: FileText,
                title: 'Automated Compliance',
                description: 'Stay compliant with automatic document generation and audit trails',
                color: 'from-purple-400 to-pink-500'
              },
              {
                icon: TrendingUp,
                title: 'Analytics & Reporting',
                description: 'Real-time insights into enrollment rates, costs, and tax savings',
                color: 'from-amber-400 to-orange-500'
              },
              {
                icon: Heart,
                title: 'Benefits Catalog',
                description: 'Showcase all available benefits with videos, FAQs, and personalized pricing',
                color: 'from-red-400 to-rose-500'
              },
              {
                icon: Clock,
                title: 'Fast Setup',
                description: 'Get up and running in minutes with our guided onboarding process',
                color: 'from-indigo-400 to-violet-500'
              }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
              >
                <Card className="h-full hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-foreground mb-4">How It Works</h2>
            <p className="text-xl text-muted-foreground">
              A simple flow for employees and HR teams.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="h-full">
              <CardContent className="p-8">
                <Badge className="mb-4 bg-blue-100 text-blue-700 text-sm px-4 py-2">
                  <Users className="w-4 h-4 mr-2" />
                  For Employees
                </Badge>
                <h3 className="text-2xl font-semibold mb-4">Enroll with confidence</h3>
                <div className="space-y-3 text-muted-foreground">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p>Sign in securely to your account.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p>Enter your invitation code to link to your employer’s program.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p>Review benefits, confirm selections, and complete enrollment.</p>
                  </div>
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {featureFlags.allow_individual_registration && (
                    <Button
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      onClick={() => api.auth.redirectToLogin('/InviteCodeEntry')}
                    >
                      Accept invite
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => (window.location.href = '/me')}>
                    Learn more
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="h-full">
              <CardContent className="p-8">
                <Badge className="mb-4 bg-emerald-100 text-emerald-700 text-sm px-4 py-2">
                  <ClipboardList className="w-4 h-4 mr-2" />
                  For HR Teams
                </Badge>
                <h3 className="text-2xl font-semibold mb-4">Run enrollment smoothly</h3>
                <div className="space-y-3 text-muted-foreground">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p>Complete company onboarding and configure enrollment.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p>Invite employees to join using your organization code.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <p>Track progress and follow up where needed.</p>
                  </div>
                </div>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {featureFlags.allow_company_registration && (
                    <Button
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      onClick={() => (window.location.href = '/CompanySignup')}
                    >
                      Get started
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => (window.location.href = '/hr')}>
                    HR overview
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/30 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground">Quick answers for first-time visitors</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                q: 'I’m an employee — how do I access my company portal?',
                a: 'Sign in, then enter the invitation code provided by your HR team to link your account to your employer.'
              },
              {
                q: 'I don’t have an invite code. What should I do?',
                a: 'Contact your HR/benefits administrator. If your company isn’t set up yet, ask them to start with company signup.'
              },
              {
                q: 'I’m HR — where do I start?',
                a: 'Use Company Signup to create your account, then follow the onboarding steps to configure enrollment and invites.'
              },
              {
                q: 'Where can I learn more before signing up?',
                a: 'Use the pages in the top navigation: Employees, HR, and Why BenefitsPro.'
              }
            ].map((item) => (
              <Card key={item.q} className="h-full">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <h3 className="text-lg font-semibold mb-2">{item.q}</h3>
                      <p className="text-muted-foreground">{item.a}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-500 border-0">
            <CardContent className="p-12">
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Benefits?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Join thousands of companies saving time and money with BenefitsPro
              </p>
              {featureFlags.allow_company_registration && (
                <Button 
                  size="lg"
                  onClick={() => window.location.href = '/CompanySignup'}
                  className="bg-background text-emerald-600 hover:bg-muted text-lg px-8 py-6"
                >
                  Get Started for Employers
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-muted border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-foreground">© 2025 BenefitsPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}